"use client";
import type { IImage } from "@components/types/media";
import type { MediaRemoteControl } from "@vidstack/react";
import type { VariantProps } from "class-variance-authority";
import type React from "react";

import { AspectRatio } from "@components/core/AspectRatio";
import { Image } from "@components/core/Image";
import Pause24 from "@ui/icons/24x24/pause-24.svg";
import Play24 from "@ui/icons/24x24/play-24.svg";
import Volume24 from "@ui/icons/24x24/volume-24.svg";
import VolumeMute24 from "@ui/icons/24x24/volume-mute-24.svg";
import { isValidImage } from "@ui/lib/image";
import { useInView } from "@ui/lib/useInView";
import { cn } from "@ui/lib/utils";
import { cva } from "class-variance-authority";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";

import type { VideoPlayerProps } from "./VideoPlayer";

const VideoPlayer = dynamic(() => import("./VideoPlayer"));

const videoPlayerButtonVariants = cva(
  "pointer-events-auto inline-flex items-center shadow-sm rounded-full effect-transform group-hover:scale-105 hover:scale-105  effect-focus",
  {
    variants: {
      inverse: {},
      size: {
        sm: "min-h-48 gap-12 p-4",
        lg: "min-h-60 gap-16 p-8",
      },
      displayLabel: {
        true: "",
        false: "",
      },
      revealOnGroupHover: {
        true: "opacity-0 group-hover:opacity-100",
        false: "",
      },
      transparent: {
        true: "bg-overlay-light text-inverse",
        false: "bg-default text-default",
      },
    },
    compoundVariants: [
      {
        displayLabel: true,
        size: "sm",
        className: "pr-16",
      },
      {
        displayLabel: true,
        size: "lg",
        className: "pr-20",
      },
    ],
    defaultVariants: {
      size: "sm",
    },
  },
);

const ToggleMuteButton = ({
  isMuted,
  className,
  onClick,
}: {
  className?: string;
  isMuted: boolean;
  onClick: () => void;
}) => {
  return (
    <button
      className={cn(
        "effect-transform effect-focus flex h-40 w-40 items-center justify-center rounded-full bg-default text-default shadow-sm duration-300 ease-in-out hover:scale-105 group-hover:scale-105",
        className,
      )}
      onClick={onClick}
    >
      {isMuted ? <VolumeMute24 /> : <Volume24 />}
      <span className='sr-only'>{isMuted ? "Unmute" : "Mute"}</span>
    </button>
  );
};

interface VideoPlayerButtonProps extends VariantProps<typeof videoPlayerButtonVariants> {
  className?: string;
  isPlaying: boolean;
  onClick?: (e: React.MouseEvent) => void;
  revealOnGroupHover?: boolean;
}

const VideoPlayerButton = ({
  isPlaying,
  size,
  displayLabel = true,
  revealOnGroupHover = false,
  className,
  transparent = false,
  onClick,
}: VideoPlayerButtonProps) => {
  const t = useTranslations("ClientKeys.Globals");
  return (
    <button
      aria-label={isPlaying ? "Pause" : "Play"}
      className={cn(
        videoPlayerButtonVariants({
          size,
          className,
          transparent,
          displayLabel,
          revealOnGroupHover: revealOnGroupHover && isPlaying,
        }),
      )}
      onClick={onClick}
    >
      <span
        className={cn(
          "flex items-center justify-center rounded-full",
          displayLabel ? "bg-inverse text-inverse" : "",
          size === "lg" ? "h-60 w-60" : "h-40 w-40",
        )}
      >
        {isPlaying ? <Pause24 /> : <Play24 />}
      </span>
      {displayLabel && (
        <span aria-hidden='true' className='f-ui-04'>
          {isPlaying ? t("pause") : t("play")}
        </span>
      )}
    </button>
  );
};

VideoPlayerButton.displayName = "VideoPlayerButton";

interface LazyVideoPlayerProps extends VideoPlayerProps {
  className?: string;
  controls?: boolean;
  cover?: IImage;
  /** When true, the player fills its parent container (no aspect ratio, no border/rounding). */
  fill?: boolean;
  overlay?: React.ReactNode;
  playButton?: ({
    isPlaying,
    togglePlay,
  }: {
    isPlaying: boolean;
    togglePlay: (event: React.MouseEvent<HTMLButtonElement>) => void;
  }) => React.ReactNode;
  rounded?: boolean;
  transparentButton?: boolean;
  unmuteButton?: ({
    isMuted,
    toggleMute,
  }: {
    isMuted: boolean;
    toggleMute: (event: React.MouseEvent<HTMLButtonElement>) => void;
  }) => React.ReactNode;
  withBackground?: boolean;
  withBorder?: boolean;
  withPlayButton?: boolean;
  withPlayButtonLabels?: boolean;
  withUnmuteButton?: boolean;
  /** Callback when video starts playing */
  onPlay?: () => void;
  /** Callback when video ends */
  onEnded?: () => void;
}

const LazyVideoPlayer = ({
  autoplay = false,
  cover,
  className,
  fill = false,
  withBorder = true,
  ratio = "16x9",
  controls = true,
  withUnmuteButton = false,
  withPlayButtonLabels = true,
  withPlayButton = true,
  transparentButton = false,
  playButton,
  unmuteButton,
  overlay,
  rounded = true,
  onPlay,
  onEnded,
  ...props
}: LazyVideoPlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState<boolean>(!!props.muted);
  const [remote, setRemote] = useState<MediaRemoteControl | null>(null);

  const intersectionRef = useRef<HTMLDivElement>(null);

  const hasCover = isValidImage(cover);
  const [showCover, setShowCover] = useState(!autoplay);
  const [coverLoaded, setCoverLoaded] = useState(!hasCover || cover?.loading === "eager" || autoplay);
  const [shouldRenderPlayer, setShouldRenderPlayer] = useState(false);

  const inView = useInView(intersectionRef, { once: true });

  useEffect(() => {
    if (inView) {
      setShouldRenderPlayer(true);
    }
  }, [inView]);

  const onAutoPlayFail = () => {
    setShowCover(true);
  };

  const togglePlay = (event: Event) => {
    if (!shouldRenderPlayer) {
      setShouldRenderPlayer(true);
    }
    isPlaying ? remote?.pause(event) : remote?.play(event);
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    isMuted ? remote?.unmute() : remote?.mute();
    setIsMuted(!isMuted);
  };

  const setRemoteCallback = useCallback(
    (remote: MediaRemoteControl) => {
      setRemote(remote);
      props.setRemote?.(remote);
    },
    [props.setRemote],
  );

  const content = (
    <>
      {shouldRenderPlayer && (
        <VideoPlayer
          {...props}
          autoplay={autoplay || isPlaying}
          controls={controls}
          coverLoaded={coverLoaded}
          fill={fill}
          hasCover={hasCover}
          muted={isMuted}
          onAutoPlayFail={onAutoPlayFail}
          onPlay={onPlay}
          onEnded={onEnded}
          ratio={ratio}
          setIsMuted={setIsMuted}
          setIsPlaying={setIsPlaying}
          setRemote={setRemoteCallback}
        />
      )}

      {hasCover && showCover && (
        <Image
          className={cn(
            "effect-opacity absolute -bottom-2 -left-2 -right-2 -top-2 z-10 max-w-none cursor-pointer bg-default",
            isPlaying ? "pointer-events-none opacity-0" : "",
          )}
          onClick={event => {
            if (isPlaying) return;
            togglePlay(event as any);
          }}
          onReveal={() => {
            setCoverLoaded(true);
          }}
          style={{ width: "calc(100% + 4px)", height: "calc(100% + 4px)" }}
          {...cover}
        />
      )}

      {!controls && withUnmuteButton && !unmuteButton && (
        <div
          className={cn(
            "absolute bottom-12 left-12 z-2",
            !cover && !isPlaying && !autoplay ? "bg-overlay-lighter" : "",
            coverLoaded ? "" : "hidden",
          )}
        >
          <ToggleMuteButton isMuted={isMuted} onClick={toggleMute} />
        </div>
      )}

      {unmuteButton && unmuteButton({ isMuted, toggleMute })}

      {withPlayButton && (
        <div
          className={cn(
            "pointer-events-none absolute inset-0 z-20 flex items-center justify-center",
            !cover && !isPlaying && !autoplay ? "bg-overlay-lighter" : "",
            coverLoaded ? "" : "hidden",
            controls && isPlaying ? "hidden" : "",
          )}
        >
          {playButton ? (
            playButton({ isPlaying, togglePlay: e => togglePlay(e as any) })
          ) : (
            <VideoPlayerButton
              displayLabel={withPlayButtonLabels}
              isPlaying={isPlaying}
              onClick={event => togglePlay(event as any)}
              revealOnGroupHover={showCover ? false : controls || autoplay}
              transparent={transparentButton}
            />
          )}
        </div>
      )}
      {overlay && overlay}
    </>
  );

  return (
    <div className={cn("group relative w-full", fill && "h-full", className)} ref={intersectionRef}>
      {fill ? (
        <div className={cn("relative h-full overflow-hidden", !hasCover && "bg-dark")}>{content}</div>
      ) : (
        <AspectRatio
          border={withBorder}
          className={cn("relative overflow-hidden", !hasCover && "bg-dark")}
          ratio={ratio as any}
          rounded={rounded}
        >
          {content}
        </AspectRatio>
      )}
    </div>
  );
};

LazyVideoPlayer.displayName = "LazyVideoPlayer";

export { LazyVideoPlayer };
export default LazyVideoPlayer;
